import React, { useState } from "react";
import { ReservationNavigation } from "../components/ReservationNavigation";
import { FilterMultiSelect } from "@/components/common/FilterMultiSelect";
import { Button } from "@/components/ui/button";
import { PlusCircle, Upload } from "lucide-react";
import { useDateRange } from "@/hooks/useDateRange";
import { useCSVExport } from "@/hooks/useCSVExport";
import { useSorting } from "@/hooks/useSorting";
import {
  useGuestNationalities,
  useGuests,
} from "@/components/pages/reservations/guests/hooks/useGuests";
import { useGuestCreate } from "@/components/pages/reservations/guests/hooks/useGuestCreate";
import { GuestsTable } from "./components/GuestsTable";
import GuestModal from "./components/GuestModal";
import * as countries from "i18n-iso-countries";
// @ts-expect-error Missing type definitions for locale file
import enLocale from "i18n-iso-countries/langs/en.json";
import { useHotel } from "@/hooks/useHotel";
import { GuestStatus } from "@/types/guest";
import { DateRangePicker } from "@/components/common/DateRangePicker";

countries.registerLocale(enLocale);

const perPage = 10;

export const GuestsPage: React.FC = () => {
  const { dateRange, setDateRange } = useDateRange();
  const { exportToCSV } = useCSVExport();
  const { sortField, sortDirection, handleSort } = useSorting();
  const [guestStatus, setGuestStatus] = useState<GuestStatus[]>([]);
  const [nationality, setNationality] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const { hotelId } = useHotel();

  const { data: nationalitiesData } = useGuestNationalities(hotelId);

  const { data: guestsResponse } = useGuests(hotelId, {
    dateRange,
    sortField,
    sortDirection,
    guestStatus,
    nationality,
    perPage,
    currentPage,
  });

  const { handleGuestSubmit } = useGuestCreate(hotelId);

  const guests = guestsResponse?.guests || [];
  const totalPages = guestsResponse?.totalPages || 0;

  const nationalityOptions = (nationalitiesData || []).map((countryCode) => ({
    value: countryCode,
    label: countries.getName(countryCode, "en") || countryCode,
  }));

  const handleCSVExport = () => {
    exportToCSV(guests, "guests-export.csv");
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-12 p-12">
      <ReservationNavigation activeTab="guests" />

      <div className={"flex flex-col gap-6"}>
        <div className={"flex min-h-12 flex-row items-center justify-between"}>
          <div className={"flex h-full flex-row items-center gap-x-4"}>
            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              triggerVariant="new_outline"
            />
            <FilterMultiSelect
              options={Object.values(GuestStatus).map((value) => ({
                value,
                label: value,
              }))}
              placeholder={"Guest Status"}
              value={guestStatus}
              onValueChange={(value) => setGuestStatus(value as GuestStatus[])}
            />
            {!!nationalityOptions.length && (
              <FilterMultiSelect
                options={nationalityOptions}
                placeholder={"Nationality"}
                value={nationality}
                onValueChange={setNationality}
                className="min-w-[110px]"
              />
            )}
          </div>
          <div className={"flex flex-row items-center gap-x-4"}>
            <GuestModal
              trigger={
                <Button variant="new_default">
                  <PlusCircle />
                  Add guest
                </Button>
              }
              onSubmit={handleGuestSubmit}
            />
            <Button variant="new_outline" onClick={handleCSVExport}>
              <Upload />
              Upload
            </Button>
          </div>
        </div>

        <GuestsTable
          data={guests}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};
